import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaTree, FaCheckCircle, FaChevronLeft, FaChevronRight, FaSeedling, FaLeaf } from 'react-icons/fa';
import { GiGrass, GiFlowers, GiPalmTree, GiCactus, GiForest, GiSpade, GiWateringCan } from 'react-icons/gi';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { useRef, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactSection from '../components/ContactSection';
import ImageLightbox from '../components/ImageLightbox';
import WhatsAppButton from '../components/WhatsAppButton';
import CatalogModal from '../components/CatalogModal';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const Paisagismo = () => {
  const [heroRef, heroInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const paisagismoSwiperRef = useRef<SwiperType | null>(null);
  const arvoresSwiperRef = useRef<SwiperType | null>(null);
  const jardinavemSwiperRef = useRef<SwiperType | null>(null);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState({ src: '', alt: '', images: [] as string[], currentIndex: 0 });

  // Catalog modal state
  const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);

  const openLightbox = (src: string, alt: string, images: string[], index: number) => {
    setLightboxImage({ src, alt, images, currentIndex: index });
    setLightboxOpen(true);
  };

  const handlePreviousImage = () => {
    const newIndex = lightboxImage.currentIndex > 0 ? lightboxImage.currentIndex - 1 : lightboxImage.images.length - 1;
    setLightboxImage({
      ...lightboxImage,
      src: lightboxImage.images[newIndex],
      alt: `Imagem ${newIndex + 1}`,
      currentIndex: newIndex
    });
  };

  const handleNextImage = () => {
    const newIndex = lightboxImage.currentIndex < lightboxImage.images.length - 1 ? lightboxImage.currentIndex + 1 : 0;
    setLightboxImage({
      ...lightboxImage,
      src: lightboxImage.images[newIndex],
      alt: `Imagem ${newIndex + 1}`,
      currentIndex: newIndex
    });
  };

  // Paisagismo Images
  const paisagismoImages = [
    '/images/Paisagismo/A276B450-A007-4250-B22A-39544B2FDEB0.jpg',
    '/images/Paisagismo/f65f6bb1-bc79-479d-96ea-f33e06bb1992.jpg',
    '/images/Paisagismo/IMG_5909.jpg',
  ];

  // Árvores e Plantas Images
  const arvoresImages = [
    '/images/Árvores e plantas/_DSC2739.jpeg',
    '/images/Árvores e plantas/_DSC2751.jpeg',
    '/images/Árvores e plantas/_DSC2808.jpeg',
    '/images/Árvores e plantas/_DSC2829.jpeg',
    '/images/Árvores e plantas/_DSC2847.jpeg',
    '/images/Árvores e plantas/_DSC2862.jpeg',
    '/images/Árvores e plantas/_DSC2871.jpeg',
    '/images/Árvores e plantas/_DSC2873.jpeg',
    '/images/Árvores e plantas/63B59BB3-E51A-41EF-9D4E-62C06A6A1308.jpg',
  ];

  // Jardinagem Images
  const jardinavemImages = [
    '/images/Jardinagem/64620bd3-b3aa-434b-b26b-91476a0a355b.jpg',
    '/images/Jardinagem/b7a3db0c-1b17-4685-834d-5deea11c484b.jpg',
    '/images/Jardinagem/c6b3fec9-5767-4777-8b60-c0c0fb195265.jpg',
    '/images/Jardinagem/ea138241-823a-45fb-9195-d6f1bc26fc28.jpg',
    '/images/Jardinagem/fa860319-30e2-4f4c-93da-bcabba9d80ef.jpg',
    '/images/Jardinagem/IMG_1340.jpg',
    '/images/Jardinagem/IMG_1920.jpg',
    '/images/Jardinagem/IMG_1921.jpg',
    '/images/Jardinagem/IMG_1922.jpg',
  ];

  const paisagismoServices = [
    {
      icon: FaSeedling,
      title: 'Planejamento e Design',
      description: 'Desenvolvimento completo de projetos personalizados para residências, condomínios e empresas',
    },
    {
      icon: GiSpade,
      title: 'Execução e Implantação',
      description: 'Execução profissional do projeto com plantas selecionadas e materiais de primeira qualidade',
    },
    {
      icon: GiFlowers,
      title: 'Reforma de Jardins',
      description: 'Revitalização completa de jardins existentes com novo design e vegetação',
    },
  ];

  const CarouselNavButton = ({
    direction,
    onClick
  }: {
    direction: 'prev' | 'next';
    onClick: () => void
  }) => (
    <button
      onClick={onClick}
      className="hidden md:block absolute top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-natureza hover:text-white text-floresta p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
      style={{ [direction === 'prev' ? 'left' : 'right']: '-20px' }}
    >
      {direction === 'prev' ? <FaChevronLeft size={20} /> : <FaChevronRight size={20} />}
    </button>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section - Modernizado */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image com Parallax Effect */}
        <div className="absolute inset-0">
          <img
            src="/images/Árvores e plantas/_DSC2829.jpeg"
            alt="Paisagismo Hero"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-floresta/90 via-floresta/70 to-natureza/80" />

          {/* Decorative Elements */}
          <motion.div
            animate={{
              y: [0, -20, 0],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-20 right-20 w-64 h-64 bg-argila/20 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              y: [0, 20, 0],
              opacity: [0.2, 0.4, 0.2]
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }}
            className="absolute bottom-20 left-20 w-96 h-96 bg-conforto/20 rounded-full blur-3xl"
          />
        </div>

        {/* Content */}
        <motion.div
          ref={heroRef}
          initial={{ opacity: 0, y: 50 }}
          animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 1 }}
          className="container-custom relative z-10 text-center text-white"
        >
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={heroInView ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
            transition={{ duration: 0.8, delay: 0.3, type: "spring" }}
            className="inline-block mb-8"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-argila/30 blur-2xl rounded-full" />
              <FaTree className="text-7xl md:text-8xl text-argila relative z-10 drop-shadow-2xl" />
            </div>
          </motion.div>

          <motion.h1
            className="font-display mb-6 text-5xl md:text-7xl"
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            Paisagismo & <span className="text-argila italic">Manutenção</span>
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl max-w-3xl mx-auto font-light leading-relaxed"
            initial={{ opacity: 0 }}
            animate={heroInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            Criamos e cuidamos de jardins que transformam ambientes em verdadeiros refúgios naturais
          </motion.p>

          <motion.div
            className="mt-10"
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ delay: 0.9, duration: 0.8 }}
          >
            <a
              href="#contato"
              className="btn-primary text-lg px-8 py-4 hover:scale-105 inline-block"
            >
              Solicitar Orçamento
            </a>
          </motion.div>
        </motion.div>
      </section>


      {/* Manutenção e Jardinagem Section */}
      <section className="section-padding bg-gradient-to-br from-white to-conforto/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Header */}
            <div className="flex items-center gap-4 mb-12">
              <div className="bg-gradient-to-br from-natureza to-floresta p-4 rounded-2xl text-white">
                <GiWateringCan className="text-5xl" />
              </div>
              <div className="flex-1">
                <h2 className="text-floresta font-display text-3xl md:text-4xl lg:text-5xl">
                  <span className="text-natureza italic">Manutenção & Jardinagem</span>
                </h2>
                <p className="text-jardim text-lg mt-2">
                  Cuidado profissional para manter seu jardim sempre belo e saudável
                </p>
              </div>
            </div>

            {/* Carousel */}
            <div className="relative mb-12">
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={30}
                slidesPerView={1}
                breakpoints={{
                  640: { slidesPerView: 2 },
                  1024: { slidesPerView: 3 },
                }}
                autoplay={{ delay: 5000, disableOnInteraction: false }}
                pagination={{ clickable: true }}
                onSwiper={(swiper) => { jardinavemSwiperRef.current = swiper; }}
                className="!pb-12"
              >
                {jardinavemImages.map((img, index) => (
                  <SwiperSlide key={img}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5 }}
                      className="group relative overflow-hidden rounded-2xl shadow-xl aspect-[4/3] cursor-pointer"
                      onClick={() => openLightbox(img, `Manutenção ${index + 1}`, jardinavemImages, index)}
                    >
                      <img
                        src={img}
                        alt={`Manutenção ${index + 1}`}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-floresta/80 via-floresta/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </motion.div>
                  </SwiperSlide>
                ))}
              </Swiper>

              <CarouselNavButton
                direction="prev"
                onClick={() => jardinavemSwiperRef.current?.slidePrev()}
              />
              <CarouselNavButton
                direction="next"
                onClick={() => jardinavemSwiperRef.current?.slideNext()}
              />
            </div>

            {/* Services */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              {/* Planos de Manutenção */}
              <div className="bg-gradient-to-br from-conforto/20 via-white to-natureza/5 p-8 md:p-10 rounded-3xl border border-natureza/10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-gradient-to-br from-natureza to-floresta p-3 rounded-xl">
                    <GiGrass className="text-white text-3xl" />
                  </div>
                  <h3 className="text-floresta font-display text-2xl">Planos de Manutenção</h3>
                </div>
                <p className="text-jardim mb-6 text-lg">
                  Oferecemos planos personalizados de manutenção mensal, quinzenal ou semanal para <strong className="text-natureza">empresas, condomínios e residências</strong>
                </p>
                <ul className="space-y-4">
                  {[
                    'Poda técnica e estética',
                    'Adubação e nutrição das plantas',
                    'Controle de pragas e doenças',
                    'Limpeza e retirada de folhas',
                    'Capina e roçada'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="bg-gradient-to-br from-natureza to-floresta p-1.5 rounded-lg flex-shrink-0 mt-0.5">
                        <FaCheckCircle className="text-white text-sm" />
                      </div>
                      <span className="text-jardim font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Serviços Adicionais */}
              <div className="bg-gradient-to-br from-natureza/5 via-white to-conforto/20 p-8 md:p-10 rounded-3xl border border-natureza/10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-gradient-to-br from-natureza to-floresta p-3 rounded-xl">
                    <FaLeaf className="text-white text-3xl" />
                  </div>
                  <h3 className="text-floresta font-display text-2xl">Serviços Adicionais</h3>
                </div>
                <p className="text-jardim mb-6 text-lg">
                  Além da manutenção regular, oferecemos serviços especializados com soluções personalizadas para cada tipo de ambiente
                </p>
                <ul className="space-y-4">
                  {[
                    'Instalação e manutenção de gramados',
                    'Sistemas de irrigação automatizada',
                    'Iluminação de jardins',
                    'Tratamento de solo e drenagem',
                    'Consultoria fitossanitária'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="bg-gradient-to-br from-natureza to-floresta p-1.5 rounded-lg flex-shrink-0 mt-0.5">
                        <FaCheckCircle className="text-white text-sm" />
                      </div>
                      <span className="text-jardim font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>


      {/* Projeto Paisagismo Section */}
      <section className="section-padding bg-conforto/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Header */}
            <div className="flex items-center gap-4 mb-12">
              <div className="bg-gradient-to-br from-natureza to-floresta p-4 rounded-2xl text-white">
                <GiPalmTree className="text-5xl" />
              </div>
              <div className="flex-1">
                <h2 className="text-floresta font-display text-3xl md:text-4xl lg:text-5xl">
                  Projeto <span className="text-natureza italic">Paisagismo</span>
                </h2>
                <p className="text-jardim text-lg mt-2">
                  Design exclusivo que harmoniza natureza, estética e funcionalidade
                </p>
              </div>
            </div>

            {/* Services Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              {paisagismoServices.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                  className="group relative bg-gradient-to-br from-white to-conforto/20 p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-natureza/10"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-natureza/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative">
                    <div className="bg-gradient-to-br from-natureza to-floresta p-4 rounded-xl inline-block mb-4">
                      <service.icon className="text-5xl text-white" />
                    </div>
                    <h3 className="text-floresta font-display text-xl mb-3">{service.title}</h3>
                    <p className="text-jardim">{service.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Carousel */}
            <div className="relative mb-12">
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={30}
                slidesPerView={1}
                breakpoints={{
                  640: { slidesPerView: 2 },
                  1024: { slidesPerView: 3 },
                }}
                autoplay={{ delay: 4000, disableOnInteraction: false }}
                pagination={{ clickable: true }}
                onSwiper={(swiper) => { paisagismoSwiperRef.current = swiper; }}
                className="!pb-12"
              >
                {paisagismoImages.map((img, index) => (
                  <SwiperSlide key={img}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5 }}
                      className="group relative overflow-hidden rounded-2xl shadow-xl aspect-[4/3] cursor-pointer"
                      onClick={() => openLightbox(img, `Projeto Paisagismo ${index + 1}`, paisagismoImages, index)}
                    >
                      <img
                        src={img}
                        alt={`Projeto Paisagismo ${index + 1}`}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-floresta/80 via-floresta/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </motion.div>
                  </SwiperSlide>
                ))}
              </Swiper>

              <CarouselNavButton
                direction="prev"
                onClick={() => paisagismoSwiperRef.current?.slidePrev()}
              />
              <CarouselNavButton
                direction="next"
                onClick={() => paisagismoSwiperRef.current?.slideNext()}
              />
            </div>

            {/* Features */}
            <div className="bg-gradient-to-br from-natureza/5 via-white to-conforto/20 p-8 md:p-10 rounded-3xl border border-natureza/10">
              <h3 className="text-floresta font-display text-2xl md:text-3xl mb-8">O que inclui nosso projeto</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  'Visita técnica e análise do terreno',
                  'Projeto 3D com visualização realista',
                  'Seleção de espécies adequadas ao clima',
                  'Planejamento de irrigação e iluminação',
                  'Especificação de materiais e acabamentos',
                  'Acompanhamento completo da execução'
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="bg-gradient-to-br from-natureza to-floresta p-2 rounded-lg flex-shrink-0">
                      <FaCheckCircle className="text-white" />
                    </div>
                    <span className="text-jardim font-medium pt-1">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>


      {/* Árvores e Plantas Section */}
      <section className="section-padding bg-gradient-to-br from-conforto/30 to-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Header */}
            <div className="flex items-center gap-4 mb-12">
              <div className="bg-gradient-to-br from-natureza to-floresta p-4 rounded-2xl text-white">
                <FaSeedling className="text-5xl" />
              </div>
              <div className="flex-1">
                <h2 className="text-floresta font-display text-3xl md:text-4xl lg:text-5xl">
                  Árvores & <span className="text-natureza italic">Plantas</span>
                </h2>
                <p className="text-jardim text-lg mt-2">
                  Fornecimento de mudas selecionadas e árvores de grande porte
                </p>
              </div>
            </div>

            {/* Carousel */}
            <div className="relative mb-12">
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={30}
                slidesPerView={1}
                breakpoints={{
                  640: { slidesPerView: 2 },
                  1024: { slidesPerView: 3 },
                }}
                autoplay={{ delay: 4500, disableOnInteraction: false }}
                pagination={{ clickable: true }}
                onSwiper={(swiper) => { arvoresSwiperRef.current = swiper; }}
                className="!pb-12"
              >
                {arvoresImages.map((img, index) => (
                  <SwiperSlide key={img}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5 }}
                      className="group relative overflow-hidden rounded-2xl shadow-xl aspect-[4/3] cursor-pointer"
                      onClick={() => openLightbox(img, `Árvores e Plantas ${index + 1}`, arvoresImages, index)}
                    >
                      <img
                        src={img}
                        alt={`Árvores e Plantas ${index + 1}`}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-floresta/80 via-floresta/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </motion.div>
                  </SwiperSlide>
                ))}
              </Swiper>

              <CarouselNavButton
                direction="prev"
                onClick={() => arvoresSwiperRef.current?.slidePrev()}
              />
              <CarouselNavButton
                direction="next"
                onClick={() => arvoresSwiperRef.current?.slideNext()}
              />
            </div>

            {/* Features */}
            <div className="bg-white p-8 md:p-10 rounded-3xl shadow-2xl border border-natureza/10">
              <h3 className="text-floresta font-display text-2xl md:text-3xl mb-8">Nosso Viveiro</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-gradient-to-br from-natureza to-floresta p-3 rounded-xl">
                      <GiForest className="text-white text-2xl" />
                    </div>
                    <h4 className="text-natureza font-semibold text-lg">Árvores de Grande Porte</h4>
                  </div>
                  <ul className="space-y-3">
                    {[
                      'Palmeiras imperiais, jerivás e fênix',
                      'Ipês amarelos, roxos e brancos',
                      'Jacarandás, quaresmeiras e resedás',
                      'Árvores frutíferas diversas'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <FaCheckCircle className="text-natureza mt-1 flex-shrink-0 text-sm" />
                        <span className="text-jardim">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-gradient-to-br from-natureza to-floresta p-3 rounded-xl">
                      <GiCactus className="text-white text-2xl" />
                    </div>
                    <h4 className="text-natureza font-semibold text-lg">Plantas Ornamentais</h4>
                  </div>
                  <ul className="space-y-3">
                    {[
                      'Arbustos e forrações',
                      'Plantas tropicais e exóticas',
                      'Flores de época e jardim',
                      'Suculentas e cactos'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <FaCheckCircle className="text-natureza mt-1 flex-shrink-0 text-sm" />
                        <span className="text-jardim">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-8 p-6 bg-gradient-to-r from-natureza/10 via-conforto/20 to-transparent rounded-2xl border-l-4 border-natureza">
                <p className="text-jardim text-lg">
                  <strong className="text-natureza">Todas as mudas</strong> são rigorosamente selecionadas,
                  cultivadas em nosso viveiro e entregues com garantia de qualidade e adaptação.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>


      {/* Catalog CTA Section */}
      <section className="section-padding bg-gradient-to-br from-floresta via-natureza to-floresta text-white relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-argila/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-conforto/10 rounded-full blur-3xl" />

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="inline-block mb-6"
            >
              <GiPalmTree className="text-6xl text-argila" />
            </motion.div>

            <h2 className="font-display text-3xl md:text-4xl mb-4">
              Conheça todos os nossos <span className="italic text-argila">serviços</span>
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Acesse nosso catálogo completo e descubra a solução perfeita para seu projeto
            </p>

            <button
              onClick={() => setIsCatalogModalOpen(true)}
              className="btn-outline inline-block bg-white/10 hover:bg-white/20 backdrop-blur-sm border-white/50 hover:border-white text-white"
            >
              Ver Catálogo Completo
            </button>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <ContactSection />

      {/* Footer */}
      <Footer />

      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        imageSrc={lightboxImage.src}
        imageAlt={lightboxImage.alt}
        onClose={() => setLightboxOpen(false)}
        onPrevious={handlePreviousImage}
        onNext={handleNextImage}
        hasPrevious={lightboxImage.images.length > 1}
        hasNext={lightboxImage.images.length > 1}
      />

      {/* Catalog Modal */}
      <CatalogModal
        isOpen={isCatalogModalOpen}
        onClose={() => setIsCatalogModalOpen(false)}
      />

      {/* WhatsApp Button */}
      <WhatsAppButton />

      {/* Custom Swiper Styles */}
      <style>{`
        .swiper-pagination-bullet {
          background: #c9ab8f;
          opacity: 0.5;
        }
        .swiper-pagination-bullet-active {
          background: #01544c;
          opacity: 1;
        }
      `}</style>
    </div>
  );
};

export default Paisagismo;
